import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ROUTER_NAVIGATION } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import { CrudStatus } from '@wdx/shared/utils';
import { filter, map, withLatestFrom } from 'rxjs/operators';
import { RouterStateAndUrl } from './custom-route-serializer';
import {
    refreshExtendedRouteState,
    updateExtendedRouteState,
} from './extended-route.actions';
import { selectRouterState } from './router.selector';
import { SITEMAP_MATCHER_SERVICE } from './sitemap-matcher.service';

/**
 * Effect that watches router state changes and updates extended route state
 * with sitemap data when available.
 */
@Injectable()
export class ExtendedRouteEffects {
    private actions$ = inject(Actions);
    private store$ = inject(Store);
    private sitemapMatcher = inject(SITEMAP_MATCHER_SERVICE, {
        optional: true,
    });

    /**
     * Helper method to compute extended route state from current router state
     */
    private computeExtendedRouteState(
        routeState: RouterStateAndUrl | undefined,
    ) {
        if (
            !routeState ||
            !routeState.path ||
            routeState.path.length === 0 ||
            !this.sitemapMatcher
        ) {
            return updateExtendedRouteState({
                status: CrudStatus.Success,
                sitemapEntry: undefined,
                parentSitemapEntries: undefined,
                wholeSitemap: undefined,
                isAdminArea: undefined,
            });
        }

        const path = routeState.path;
        const isAdminArea =
            path.length > 1 && path[1] === 'system-administration';

        const matchedSitemap = this.sitemapMatcher.matchPathToSitemap(
            path,
            isAdminArea,
        );
        const fullSitemap = this.sitemapMatcher.getFullSitemap(isAdminArea);

        // If sitemap is not loaded yet, set status to Pending (waiting for sitemap)
        if (!fullSitemap) {
            return updateExtendedRouteState({
                status: CrudStatus.Pending,
                sitemapEntry: undefined,
                parentSitemapEntries: undefined,
                wholeSitemap: undefined,
                isAdminArea,
            });
        }

        if (matchedSitemap && matchedSitemap.length > 0) {
            const validEntries = matchedSitemap.filter(
                (entry): entry is NonNullable<typeof entry> =>
                    entry !== undefined && entry !== null,
            );

            if (validEntries.length > 0) {
                const sitemapEntry = validEntries[validEntries.length - 1];
                const parentSitemapEntries =
                    validEntries.length > 1 ? validEntries.slice(0, -1) : [];

                return updateExtendedRouteState({
                    status: CrudStatus.Success,
                    sitemapEntry,
                    parentSitemapEntries:
                        parentSitemapEntries.length > 0
                            ? parentSitemapEntries
                            : undefined,
                    wholeSitemap: fullSitemap,
                    isAdminArea,
                });
            }
        }

        // Route has sitemap loaded but no matching entry (e.g., /list routes)
        // This is a valid completed state - use Success
        return updateExtendedRouteState({
            status: CrudStatus.Success,
            sitemapEntry: undefined,
            parentSitemapEntries: undefined,
            wholeSitemap: fullSitemap,
            isAdminArea,
        });
    }

    /**
     * When router navigation occurs, update extended route state with sitemap data.
     * This effect reacts to ROUTER_NAVIGATION actions and synchronously checks
     * if sitemap data is available via the SitemapMatcherService.
     */
    updateExtendedRouteOnNavigation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ROUTER_NAVIGATION),
            withLatestFrom(this.store$.select(selectRouterState)),
            map(([_, routeState]) =>
                this.computeExtendedRouteState(routeState),
            ),
        ),
    );

    /**
     * When refreshExtendedRouteState action is dispatched, refresh the extended route state
     * by re-computing it from the current router state. This allows components to trigger
     * a refresh when sitemap loads after navigation.
     */
    refreshExtendedRouteState$ = createEffect(() =>
        this.actions$.pipe(
            ofType(refreshExtendedRouteState),
            withLatestFrom(this.store$.select(selectRouterState)),
            filter(([_, routeState]) => {
                // Only proceed if we have a valid route state
                return (
                    routeState !== undefined &&
                    routeState.path !== undefined &&
                    routeState.path.length > 0
                );
            }),
            map(([_, routeState]) =>
                this.computeExtendedRouteState(routeState),
            ),
        ),
    );
}
